// What a Fashion till can and can't do without a connection, in one place, so
// the toolbar, the banner and the Offline panel all say the same thing.
//
// The rule: a sale from this store's own shelves goes ahead offline (unless
// the till is set to bill only online), saved on the till and sent when it
// reconnects. Anything shared with other tills, stores or channels (holds,
// alterations, other stores' stock, online orders, customers' books, past
// bills) waits for the connection, because a copy on one till could promise
// something another till has already given away.
export const PRICES_STALE_MS = 24 * 60 * 60 * 1000;

// Toolbar screens that read or write the server's shared records.
export const NEEDS_CONNECTION = new Set(["holds", "alterations", "online", "count", "followups", "appointments", "dashboard", "orders", "customer", "estimates", "cash"]);

export const onlineBillingOnly = (posConfig) => posConfig?.disableOfflineBilling === "Y";

const HOUR = 60 * 60 * 1000;
export function ageLabel(at, now = Date.now()) {
  if (!at) return null;
  const ms = Math.max(0, now - at);
  if (ms < HOUR) return "less than an hour ago";
  const h = Math.floor(ms / HOUR);
  if (h < 48) return h === 1 ? "an hour ago" : `${h} hours ago`;
  return `${Math.floor(h / 24)} days ago`;
}

export const pricesStale = (at, now = Date.now()) => !at || now - at > PRICES_STALE_MS;

// Every rule: status "yes" (works offline), "limited" (works, with the note's
// limits) or "no" (waits for the connection).
export function offlineRules(posConfig, pricesAt, now = Date.now()) {
  const billing = !onlineBillingOnly(posConfig);
  const age = ageLabel(pricesAt, now);
  return [
    billing
      ? { key: "sell", label: "Selling from this store's stock", status: "yes", note: "Bills are saved on this till and sent to the server when it reconnects." }
      : { key: "sell", label: "Selling from this store's stock", status: "no", note: "This till is set to bill only online, so a bill can't be paid until it reconnects." },
    {
      key: "prices",
      label: "Markdowns and season prices",
      status: "limited",
      note: age ? `Uses the prices this till saved ${age}. Changes made since apply once it reconnects.` : "No markdowns are saved on this till, so items ring up at full price until it reconnects.",
    },
    { key: "parked", label: "Parking and recalling bills", status: "yes", note: "Parked bills stay on this till." },
    {
      key: "heldSizes",
      label: "Selling a size someone has on hold",
      status: "limited",
      note: "Holds can't be seen offline, so check the hold shelf before selling the last piece. A hold this bill takes off the shelf closes once the till reconnects.",
    },
    { key: "returns", label: "Returns, exchanges and gift receipts", status: "no", note: "Finding the original bill needs the server." },
    { key: "stores", label: "Other stores' stock and selling from another store", status: "no", note: "Holding stock in another store needs that store's live stock." },
    { key: "online", label: "Online orders: pickup, ship from store, returns", status: "no", note: "Online orders are on the server, shared with the online store." },
    { key: "shared", label: "Holds and alterations", status: "no", note: "Every till in the store shares them." },
    { key: "customers", label: "Customer history, notes and follow-ups", status: "no", note: "Customers' books are shared by every store." },
    { key: "count", label: "Stock counts", status: "no", note: "A count already started stays on this till until it reconnects." },
    { key: "receipts", label: "Receipts by WhatsApp or email, and the dashboard", status: "no", note: "Both come from the server." },
  ];
}

// The line under the header while the till is offline, or null when online.
export function offlineNotice(online, posConfig, pricesAt, now = Date.now()) {
  if (online) return null;
  const head = onlineBillingOnly(posConfig)
    ? "Offline. This till bills only online, so bills can't be paid until it reconnects."
    : "Offline. Bills are saved on this till and sent when it reconnects.";
  const prices = !pricesAt
    ? " No markdowns are saved here, so items ring up at full price."
    : pricesStale(pricesAt, now)
    ? ` Markdown prices were saved ${ageLabel(pricesAt, now)}.`
    : "";
  return `${head}${prices}`;
}
